/**
 * Pure rules of the controlled feature group revisions (story 186). No React, no API: the editor
 * components call these functions and the tests exercise them without mounting anything.
 *
 * Mode: CONTROLLED when the Sun setting FEATURE_GROUP_CONTROLLED_REVISIONS is 'true', FREE otherwise.
 * In CONTROLLED mode Sun assigns Version and the seals (Ready by / Approved by / Approved date); the
 * client only asks for a state and Sun validates it against the PERSISTED state (RN18, RN19).
 */

/** Name of the Sun setting that switches the mode (C1). It is read with `apiGet('settings/' + key)`. */
export const FEATURE_GROUP_CONTROLLED_REVISIONS_SETTING = 'FEATURE_GROUP_CONTROLLED_REVISIONS'

/**
 * Deduces the mode from the response of `GET settings/FEATURE_GROUP_CONTROLLED_REVISIONS` (RN1, C1).
 * Fail-closed: anything that is not the text 'true' (any case) means FREE.
 *
 * @param {{ value?: string }} [setting] Response of the settings endpoint (may be empty or malformed)
 * @returns {boolean} true when the revisions are controlled
 */
export const isFeatureGroupRevisionControlled = (setting) => {
  const value = setting?.value
  return typeof value === 'string' && value.toLowerCase() === 'true'
}

/** The three states of a feature group, in the order of the grammar of RN18. */
export const FEATURE_GROUP_STATES = ['DRAFT', 'PROPOSAL', 'APPROVED']

/** Version of a feature group that has never been saved (RN9). `version` travels as text (C2). */
export const NEW_FEATURE_GROUP_VERSION = '0'

/**
 * The persisted copy of every feature group of a workflow, by id: "the state it had when the workflow
 * was loaded or after the last successful save" (the per-group baseline of S72-S77). It is captured
 * from the loaded workflow itself, never from `originalData`, because the fabrication order editor has
 * no such copy (S113). Walks the already loaded subworkflows too.
 *
 * @param {{ nodes?: object[] }} workflow Workflow with nodes[].jobReference.taskReferences[].featureGroups[]
 * @returns {Object<string, object>} Deep copies of the feature groups, keyed by `id`
 */
export const collectFeatureGroupBaselines = (workflow) => {
  const baselines = {}
  const walk = (nodes) => {
    (nodes || []).forEach((node) => {
      (node?.jobReference?.taskReferences || []).forEach((taskReference) => {
        (taskReference?.featureGroups || []).forEach((featureGroup) => {
          if (featureGroup?.id) {
            baselines[featureGroup.id] = structuredClone(featureGroup)
          }
        })
      })
      walk(node?.subWorkflow?.nodes)
    })
  }
  walk(workflow?.nodes)
  return baselines
}

/**
 * State name of a persisted feature group, or '' when there is no baseline (a feature group created in
 * this editing session: it will be born DRAFT v0, RN9).
 *
 * @param {{ indexValue?: { name?: string } }} [baseline]
 * @returns {string} 'DRAFT' | 'PROPOSAL' | 'APPROVED' | ''
 */
export const persistedStateOf = (baseline) => baseline?.indexValue?.name || ''

/**
 * A feature group that was APPROVED when persisted has a pending new version once the editor applied the "new version"
 * warning (it is the only way to leave APPROVED, RN18/RN20): it is DRAFT in memory, or carries a Version above the persisted
 * one because the engineer then asked for PROPOSAL or APPROVED for the new version in the same save (E4-4).
 *
 * @param {object} [baseline] Persisted feature group
 * @param {object} [current] Feature group as edited in memory
 * @returns {boolean}
 */
export const hasPendingNewVersion = (baseline, current) => {
  if (persistedStateOf(baseline) !== 'APPROVED') {
    return false
  }
  const state = current?.indexValue?.name
  return state === 'DRAFT' || Number(current?.version) > Number(baseline?.version)
}

/**
 * Options of the Index selector of a feature group row in CONTROLLED mode: every index available to the user (RN23),
 * whatever the persisted state (fix: no index restriction on feature groups; Sun opens vN+1 when an APPROVED one goes
 * back). Keeping the persisted state itself is never a request, so it stays even if the user's profile lacks it.
 *
 * @param {{ baseline?: object, availableNames?: string[] }} input
 * @returns {string[]} State names to offer
 */
export const legalIndexNames = ({ baseline = {}, availableNames = [] }) => {
  const persisted = persistedStateOf(baseline)
  return persisted && !availableNames.includes(persisted) ? [...availableNames, persisted] : availableNames
}

/** Header fields that are content of a feature group (RN13). State, Version, seals and documents are not. */
const HEADER_CONTENT_FIELDS = ['code', 'name', 'description', 'revision']

/** Fields of a feature that are content (RN13), including the extended columns in both spellings. */
const FEATURE_CONTENT_FIELDS = [
  'featureOrder', 'code', 'name', 'description', 'featureType', 'qualitativeAttribute', 'frequency', 'value',
  'upperTolerance', 'lowerTolerance', 'unit', 'reference', 'referenceNumber', 'inspectionPoint', 'inspectionPointId',
  ...Array.from({ length: 12 }, (_, index) => `extendedColumn${index + 1}`),
  ...Array.from({ length: 12 }, (_, index) => `extended_column${index + 1}`)
]

/** Comparable form of a field: empty and missing are the same, numbers equal their text, objects by identity. */
const comparable = (value) => {
  if (value === undefined || value === null || value === '') {
    return ''
  }
  if (typeof value === 'object') {
    const key = value.id ?? value.code ?? value.name
    return key === undefined ? JSON.stringify(value) : String(key)
  }
  return String(value)
}

const toolSetOf = (feature) => (feature?.toolReferences || []).map(comparable).sort().join('|')

const sameFeature = (a, b) =>
  FEATURE_CONTENT_FIELDS.every((field) => comparable(a?.[field]) === comparable(b?.[field])) && toolSetOf(a) === toolSetOf(b)

/**
 * Is there a content change (RN13) between two versions of the same feature group? Compares the header and
 * the features (matched by id: an unmatched one is an addition or a removal, RN14). State, Version, seals and
 * documents are not content, so they never count.
 *
 * @param {object} [before] Feature group as it was
 * @param {object} [after] Feature group as it is now
 * @returns {boolean}
 */
export const hasFeatureGroupContentChange = (before, after) => {
  if (!HEADER_CONTENT_FIELDS.every((field) => comparable(before?.[field]) === comparable(after?.[field]))) {
    return true
  }
  const beforeFeatures = before?.features || []
  const afterFeatures = after?.features || []
  if (beforeFeatures.length !== afterFeatures.length) {
    return true
  }
  return afterFeatures.some((feature) => {
    const match = beforeFeatures.find((candidate) => candidate.id === feature.id)
    return !match || !sameFeature(match, feature)
  })
}

/**
 * Must the editor warn before applying `next`? Only when the PERSISTED state is APPROVED, the new version is
 * not already pending in memory (RN17, RN50: one warning per group and session) and `next` changes content
 * (RN13). DRAFT / PROPOSAL (RN15) and groups born in this session never warn.
 *
 * @param {{ baseline?: object, current?: object, next?: object }} input
 * @returns {boolean}
 */
export const requiresNewVersion = ({ baseline = {}, current = {}, next = {} }) =>
  persistedStateOf(baseline) === 'APPROVED' &&
  !hasPendingNewVersion(baseline, current) &&
  hasFeatureGroupContentChange(current, next)

/** The seals Sun owns in CONTROLLED mode (RN21). */
const SEAL_FIELDS = ['readyBy', 'approvedBy', 'approvedDate']

const withoutSeals = (featureGroup) => {
  const result = { ...featureGroup }
  SEAL_FIELDS.forEach((field) => delete result[field])
  return result
}

const pickSeals = (featureGroup) => Object.fromEntries(SEAL_FIELDS.filter((field) => featureGroup?.[field] !== undefined).map((field) => [field, featureGroup[field]]))

/**
 * The "new version pending" form of an APPROVED group once the warning is confirmed (CU4 step 2): the content
 * of `next`, DRAFT, Version = persisted Version + 1 and no seals. DRAFT whatever `next` asked for: approving the new
 * version is always an explicit choice made afterwards (E4-4). Nothing is persisted; the mark that the version is pending
 * is the state and the Version (see hasPendingNewVersion).
 *
 * @param {object} baseline Persisted feature group (APPROVED vN)
 * @param {object} next Feature group with the content already changed
 * @param {{ id?: *, name: string }} draftIndexValue The DRAFT index value of the user's available indexes
 * @returns {object} A new feature group (the input is not touched)
 */
export const applyPendingNewVersion = (baseline, next, draftIndexValue) => ({
  ...withoutSeals(next),
  version: String((parseInt(baseline?.version, 10) || 0) + 1),
  indexValue: draftIndexValue
})

/**
 * Prepares the feature groups of a workflow for the save payload (CU4-A3, RN16, RN20, RN21). Pure, shared by the
 * editor and by the fabrication order saves. In FREE mode the workflow goes untouched (same reference). In
 * CONTROLLED mode, per group:
 * - new version opened by an edit (Version above the persisted one) and the content is identical to the persisted one
 *   again: it goes back as APPROVED vN with the persisted seals and no new version;
 * - APPROVED taken back to DRAFT by hand (persisted Version): no client seals, Sun opens vN+1;
 * - new version pending with changed content: vN+1 in the state the engineer chose (DRAFT unless changed, E4-4) with no
 *   client seals;
 *   (a restore that lands on the persisted content drops `changeSource` / `restoredFromVersion` as well);
 * - group with no baseline (born in this session): no client seals;
 * - any other group goes untouched.
 * Walks the already loaded subworkflows and never mutates its input.
 *
 * @param {object} workflow Workflow with nodes[].jobReference.taskReferences[].featureGroups[]
 * @param {Object<string, object>} baselines Persisted copies by id (collectFeatureGroupBaselines)
 * @param {{ controlled?: boolean }} options
 * @returns {object} The workflow to send
 */
export const prepareFeatureGroupsForSave = (workflow, baselines, { controlled = false } = {}) => {
  if (!controlled) {
    return workflow
  }
  const prepareGroup = (featureGroup) => {
    const baseline = baselines?.[featureGroup?.id]
    if (!baseline) {
      return withoutSeals(featureGroup)
    }
    if (!hasPendingNewVersion(baseline, featureGroup)) {
      return featureGroup
    }
    // An APPROVED group taken back by hand keeps the persisted Version: it is a request, and Sun opens vN+1 for it
    const openedByAnEdit = Number(featureGroup.version) > Number(baseline.version)
    if (!openedByAnEdit || hasFeatureGroupContentChange(baseline, featureGroup)) {
      return withoutSeals(featureGroup)
    }
    // Back to the persisted APPROVED vN: nothing changed, so no restore is declared either (RN16, RN57)
    const { changeSource, restoredFromVersion, ...unchanged } = withoutSeals(featureGroup)
    return { ...unchanged, ...pickSeals(baseline), version: baseline.version, indexValue: baseline.indexValue }
  }
  const prepareNodes = (nodes) => (nodes || []).map((node) => ({
    ...node,
    ...(node?.jobReference
      ? {
          jobReference: {
            ...node.jobReference,
            // Only the task references that carry feature groups are rebuilt: the rest keep their exact shape
            taskReferences: (node.jobReference.taskReferences || []).map((taskReference) =>
              Array.isArray(taskReference?.featureGroups)
                ? { ...taskReference, featureGroups: taskReference.featureGroups.map(prepareGroup) }
                : taskReference
            )
          }
        }
      : {}),
    ...(node?.subWorkflow?.nodes ? { subWorkflow: { ...node.subWorkflow, nodes: prepareNodes(node.subWorkflow.nodes) } } : {})
  }))
  return { ...workflow, nodes: prepareNodes(workflow?.nodes) }
}

/**
 * Is the content of an approved snapshot exactly the content the group has in memory (RN32: the version identical to the
 * current content is never offered to restore)? Header and features count, matched by `code` (RN36); ids, state, version
 * and seals do not.
 *
 * @param {object} snapshot FeatureGroupRevisionSnapshotDto (C4)
 * @param {object} group Feature group as edited in memory
 * @returns {boolean}
 */
export const snapshotMatchesGroup = (snapshot, group) => {
  if (!HEADER_CONTENT_FIELDS.every((field) => comparable(snapshot?.[field]) === comparable(group?.[field]))) {
    return false
  }
  const snapshotFeatures = snapshot?.features || []
  const groupFeatures = group?.features || []
  return snapshotFeatures.length === groupFeatures.length &&
    snapshotFeatures.every((feature) => {
      const match = groupFeatures.find((candidate) => candidate.code === feature.code)
      return Boolean(match) && sameFeature(match, feature)
    })
}

/**
 * Would the header code of the snapshot be used by two groups of the task once restored (RN36, CU8-A4)? Another group
 * with that code blocks the restore; the group's own code does not.
 *
 * @param {{ snapshot: object, groups: object[], groupId: string }} input `groups` are the groups of the task as edited
 * @returns {boolean}
 */
export const restoredCodeCollides = ({ snapshot, groups, groupId }) =>
  (groups || []).some((group) => group.id !== groupId && group.code === snapshot?.code)

/**
 * Does restoring over this group open a new version? Only from APPROVED vN (RN33: DRAFT vN+1); over a draft in progress,
 * DRAFT or PROPOSAL, the content is replaced and the version stays (RN34).
 *
 * @param {object} [current] Feature group as it is in memory
 * @returns {boolean}
 */
export const restoreOpensNewVersion = (current) => current?.indexValue?.name === 'APPROVED'

/**
 * Version the group has after a restore: vN+1 from APPROVED vN, the same version otherwise (RN33, RN34). Text, as in C2.
 *
 * @param {object} [current] Feature group as it is in memory
 * @returns {string}
 */
export const restoredVersionOf = (current) =>
  restoreOpensNewVersion(current) ? String((parseInt(current?.version, 10) || 0) + 1) : String(current?.version ?? NEW_FEATURE_GROUP_VERSION)

/**
 * The feature group once the content of an approved snapshot is restored over it, in memory (CU8, RN33, RN34): the
 * snapshot header and features, DRAFT, the version of `restoredVersionOf`, no seals, and the marks Sun needs to record
 * the restore (`changeSource = RESTORE`, `restoredFromVersion`, C2). Nothing is persisted; the input is not touched.
 *
 * The features are matched by `code` (RN36): one that exists in memory keeps its id and takes every field of the snapshot,
 * one that does not is added with a new id, and one missing from the snapshot is dropped.
 *
 * @param {{ current: object, snapshot: object, version: number, draftIndexValue: { name: string }, newId: function(): string }} input
 * @returns {object} A new feature group
 */
export const restoreFeatureGroupFromSnapshot = ({ current, snapshot, version, draftIndexValue, newId }) => ({
  ...withoutSeals(current),
  code: snapshot.code,
  name: snapshot.name,
  description: snapshot.description,
  revision: snapshot.revision,
  features: (snapshot.features || []).map((feature) => {
    const match = (current?.features || []).find((candidate) => candidate.code === feature.code)
    return { ...feature, id: match ? match.id : newId() }
  }),
  version: restoredVersionOf(current),
  indexValue: draftIndexValue,
  changeSource: 'RESTORE',
  restoredFromVersion: version
})

/** Origin Sun stamps on the temporary groups of a CSV import in CONTROLLED mode (C2). */
export const CSV_IMPORT_CHANGE_SOURCE = 'CSV_IMPORT'

/**
 * Merges the groups a CSV import left in the temporary list into the groups of the task, by `code` (RN44, RN45, RN46, D-14):
 * - a group of the task whose code is imported keeps its id, state, Version and seals and takes the imported header and
 *   features (a feature that matches by code keeps its id), marked as `CSV_IMPORT`; when the content is the same nothing changes;
 * - when that group is persisted as APPROVED and has no new version pending yet, the change opens it (DRAFT vN+1, no seals,
 *   as `applyPendingNewVersion`) and is reported in `newVersions`, so that the engineer can confirm it first (RN46);
 * - an imported code the task does not have is a new group, DRAFT v0, no seals, marked as `CSV_IMPORT` (RN45);
 * - a group of the task that is not imported stays as it is.
 * Nothing is persisted and the inputs are not touched.
 *
 * @param {{ current: object[], imported: object[], baselines: Object<string, object>, draftIndexValue: { name: string } }} input
 * @returns {{ groups: object[], newVersions: { code: string, version: string }[] }} The groups of the task after the import and the new versions it opens
 */
export const mergeImportedFeatureGroups = ({ current = [], imported = [], baselines = {}, draftIndexValue }) => {
  const newVersions = []
  const edited = current.map((group) => {
    const match = imported.find((candidate) => candidate.code === group.code)
    if (!match) {
      return group
    }
    const next = {
      ...group,
      name: match.name,
      description: match.description,
      revision: match.revision,
      features: (match.features || []).map((feature) => {
        const existing = (group.features || []).find((candidate) => candidate.code === feature.code)
        return existing ? { ...feature, id: existing.id } : feature
      }),
      changeSource: CSV_IMPORT_CHANGE_SOURCE
    }
    if (!hasFeatureGroupContentChange(group, next)) {
      return group
    }
    const baseline = baselines[group.id]
    if (requiresNewVersion({ baseline, current: group, next })) {
      const opened = applyPendingNewVersion(baseline, next, draftIndexValue)
      newVersions.push({ code: opened.code, version: opened.version })
      return opened
    }
    return next
  })
  const added = imported
    .filter((group) => !current.some((candidate) => candidate.code === group.code))
    .map((group) => ({ ...withoutSeals(group), version: NEW_FEATURE_GROUP_VERSION, indexValue: draftIndexValue, changeSource: CSV_IMPORT_CHANGE_SOURCE }))
  return { groups: [...edited, ...added], newVersions }
}

/** Key of the import rejection of a duplicated code in CONTROLLED mode (C6): the message is `<key>:<code>`. */
export const IMPORT_DUPLICATE_CODE_ERROR = 'FeatureGroupImportDuplicateCode'

/**
 * Splits a rejection of the import into its translation key and the code it carries (C6, RN56). Only the FIRST ':' cuts,
 * because the repeated code may contain ':' itself. A message with no ':' is its own key, with no code.
 *
 * @param {string} [message] Message of the 400 as Sun sends it
 * @returns {{ key: string, code: string }}
 */
export const splitImportError = (message = '') => {
  const text = String(message ?? '')
  const colon = text.indexOf(':')
  return colon === -1 ? { key: text, code: '' } : { key: text.slice(0, colon), code: text.slice(colon + 1) }
}

/** How many groups of a list have each code. */
const countCodes = (groups) => groups.reduce((counts, group) => ({ ...counts, [group.code]: (counts[group.code] || 0) + 1 }), {})

/** The key Sun pairs the tasks by (`OperationUpdateFeatureCopier`): the task order, never the name; no order is the null key. */
const taskKeyOf = (task) => String(task?.taskOrder ?? null)

/**
 * The feature groups of the node by task key (RN64 p.1 [GATE 3, B9]), read through their persisted copy (`baselines`, by id): the
 * state of reference is the one the editor loaded, not the edited one. Tasks with the same key (all the tasks with no order, for
 * one) share a single list, as in Sun. `hasList` tells whether some task of the key carries a list of feature groups.
 */
const nodeGroupsByTaskKey = (nodeTasks, baselines) => nodeTasks.reduce((buckets, task) => {
  const key = taskKeyOf(task)
  const bucket = buckets[key] || { references: [], hasList: false }
  const references = (task?.featureGroups || []).map((group) => baselines?.[group?.id] || group)
  return { ...buckets, [key]: { references: [...bucket.references, ...references], hasList: bucket.hasList || Array.isArray(task?.featureGroups) } }
}, {})

/**
 * Pairs each feature group of an operation task with the groups of the node under the same task key by group `code` (D-23), as
 * Sun does: a code repeated in the operation task, or present more than once among the node groups of the key, is ambiguous and
 * pairs nothing; a code with a single candidate is a pair.
 *
 * @returns {{ operationGroup: object, reference?: object }[]}
 */
const pairOperationTaskGroups = (operationTask, references) => {
  const operationGroups = operationTask?.featureGroups || []
  const nodeCounts = countCodes(references)
  const operationCounts = countCodes(operationGroups)
  return operationGroups.map((operationGroup) => ({
    operationGroup,
    reference: nodeCounts[operationGroup.code] === 1 && operationCounts[operationGroup.code] === 1
      ? references.find((group) => group.code === operationGroup.code)
      : undefined
  }))
}

/**
 * Plans the update of the operation of a node in CONTROLLED mode (RN64, D-23, D-24): which approved feature groups of the node
 * open a new DRAFT version and which disappear. Pure; the inputs are not touched.
 *
 * @param {{ nodeJob: object, operationJob: object, baselines: Object<string, object> }} input
 *   `nodeJob` is the job of the node in the editor, `operationJob` the job of the target version of the operation and
 *   `baselines` the persisted copy of the feature groups by id (collectFeatureGroupBaselines)
 * @returns {{ newVersions: { code: string, version: string }[], removed: { code: string }[] }} `newVersions`: approved
 *   groups paired by code whose content changes, with the version they will have; `removed`: approved groups of the node
 *   that disappear (no pair in the operation, or ambiguous code, D-23). Both in the order of the tasks and groups of the
 *   node; groups in DRAFT or PROPOSAL are never listed (RN64)
 */
export const planOperationUpdate = ({ nodeJob = {}, operationJob = {}, baselines = {} }) => {
  const nodeTasks = nodeJob?.taskReferences || []
  const buckets = nodeGroupsByTaskKey(nodeTasks, baselines)
  const pairedWith = new Map()
  ;(operationJob?.taskReferences || []).forEach((operationTask) => {
    pairOperationTaskGroups(operationTask, buckets[taskKeyOf(operationTask)]?.references || []).forEach(({ operationGroup, reference }) => {
      if (reference && !pairedWith.has(reference)) {
        pairedWith.set(reference, operationGroup)
      }
    })
  })
  const newVersions = []
  const removed = []
  // In the order of the tasks and groups of the node (C6b), whatever their task key
  nodeTasks.flatMap((task) => (task?.featureGroups || []).map((group) => baselines?.[group?.id] || group)).forEach((reference) => {
    if (persistedStateOf(reference) !== 'APPROVED') {
      return
    }
    if (!pairedWith.has(reference)) {
      removed.push({ code: reference.code })
    } else if (!snapshotMatchesGroup(pairedWith.get(reference), reference)) {
      newVersions.push({ code: reference.code, version: String((parseInt(reference.version, 10) || 0) + 1) })
    }
  })
  return { newVersions, removed }
}

/**
 * The feature groups of an operation task once the update is applied, in CONTROLLED mode (RN64 steps 1-3, E2-6), for the
 * preview of the node. Per group of the operation:
 * - paired and APPROVED with other content: the content of the operation, the id of the node (its line continues), DRAFT,
 *   Version vN+1 and no seals;
 * - paired and DRAFT / PROPOSAL with other content: the content of the operation with the id, state, Version and seals of the node;
 * - paired and identical (by the visible key): the group of the node as it was, ids included;
 * - not paired (new, or ambiguous code): the content of the operation as a new group, DRAFT, Version 0, no seals, ids of its own.
 * The groups of the node that are not paired are not in the result. The features of a changed group that match one of the node
 * by `code` keep its id; the others get a new one, so no id of a library row travels with the node.
 *
 * @param {{ nodeJob: object, operationJob: object, baselines: Object<string, object>, draftIndexValue: { name: string }, newId: function(): string }} input
 * @returns {Array<object[]|undefined>} One entry per task of the operation job: its feature groups, or undefined when neither
 *   side of the task has a list of feature groups
 */
export const updatedOperationFeatureGroups = ({ nodeJob = {}, operationJob = {}, baselines = {}, draftIndexValue = { name: 'DRAFT' }, newId }) => {
  const operationTasks = operationJob?.taskReferences || []
  const resultOf = ({ operationGroup, reference }) => {
    if (!reference) {
      const { hasApprovedVersion, ...content } = withoutSeals(operationGroup)
      return {
        ...content,
        id: newId(),
        features: (operationGroup.features || []).map((feature) => ({ ...feature, id: newId() })),
        version: NEW_FEATURE_GROUP_VERSION,
        indexValue: draftIndexValue
      }
    }
    if (snapshotMatchesGroup(operationGroup, reference)) {
      return reference
    }
    const changed = {
      ...withoutSeals(operationGroup),
      id: reference.id,
      features: (operationGroup.features || []).map((feature) => {
        const match = (reference.features || []).find((candidate) => candidate.code === feature.code)
        return { ...feature, id: match ? match.id : newId() }
      }),
      ...(reference.hasApprovedVersion === undefined ? {} : { hasApprovedVersion: reference.hasApprovedVersion })
    }
    return persistedStateOf(reference) === 'APPROVED'
      ? { ...changed, version: String((parseInt(reference.version, 10) || 0) + 1), indexValue: draftIndexValue }
      : { ...changed, ...pickSeals(reference), version: reference.version, indexValue: reference.indexValue }
  }
  const buckets = nodeGroupsByTaskKey(nodeJob?.taskReferences || [], baselines)
  return operationTasks.map((operationTask) => {
    const bucket = buckets[taskKeyOf(operationTask)] || { references: [], hasList: false }
    return Array.isArray(operationTask?.featureGroups) || bucket.hasList
      ? pairOperationTaskGroups(operationTask, bucket.references).map(resultOf)
      : undefined
  })
}
